import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { RequireAuth } from 'aws-auth-lib';
import PropTypes from 'prop-types';
import Loader from '../component/Loader';
import { useRole } from '../utils/RoleContext';
import { useClinic } from '../utils/ClinicContext';
import SignOutRedirect from './SignOutRedirect';
import { getUser } from 'services/users';
import { useUser } from 'hooks/useUser';
import { selectAccess, updateUser } from 'store/userReducer';
import MainLayout from 'layout/MainLayout';

function RequiredAdmin({ children }) {
    const dispatch = useDispatch();
    const { user } = useUser();
    const { role: contextRole, setRole } = useRole();
    const { setClinic } = useClinic();

    const { status, data } = useQuery({
        queryKey: ['admin-user'],
        queryFn: getUser
    });

    useEffect(() => {
        if (data) dispatch(updateUser(data.data));
    }, [data, dispatch]);

    const adminRole = user.roles.find((role) => role.name === 'BCK_ADMIN');

    useEffect(() => {
        if (!adminRole?.account?.uuid) return;

        const clinic = adminRole.account;
        if (
            user.selectedRole !== adminRole.uuid ||
            user.selectedClinic?.uuid !== clinic.uuid
        ) {
            dispatch(selectAccess({ clinic, roleUuid: adminRole.uuid }));
        }
        setClinic(clinic);
        setRole(adminRole.name);
    }, [
        adminRole,
        dispatch,
        setClinic,
        setRole,
        user.selectedClinic?.uuid,
        user.selectedRole
    ]);

    if (status === 'error') return <SignOutRedirect />;
    if (status === 'pending' || !user.uuid) return <Loader />;
    if (!adminRole?.account?.uuid) return <SignOutRedirect />;

    if (
        user.selectedRole !== adminRole.uuid ||
        user.selectedClinic?.uuid !== adminRole.account.uuid ||
        contextRole !== adminRole.name
    ) {
        return <Loader />;
    }

    return children;
}

RequiredAdmin.propTypes = {
    children: PropTypes.node.isRequired
};

function ProtectRouteAdmin() {
    const action = () => <Navigate to="/login" replace />;

    return (
        <RequireAuth action={action}>
            <RequiredAdmin>
                <MainLayout />
            </RequiredAdmin>
        </RequireAuth>
    );
}

export { ProtectRouteAdmin };
